/**
 * EncryptionService — AES-256-GCM via Web Crypto.
 * Passwords are never stored or returned in plaintext by repositories/UI views.
 */
import { env } from '@/config'

export class EncryptionService {
  private readonly masterSecret: string

  constructor(masterSecret = resolveMasterSecret()) {
    this.masterSecret = masterSecret
  }

  async encrypt(plainText: string): Promise<string> {
    this.validate(plainText)
    const key = await this.deriveKey()
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const encoded = new TextEncoder().encode(plainText)
    const cipherBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)
    const payload = new Uint8Array(iv.length + cipherBuffer.byteLength)
    payload.set(iv, 0)
    payload.set(new Uint8Array(cipherBuffer), iv.length)
    return `aes256gcm:${toBase64(payload)}`
  }

  async decrypt(cipherText: string): Promise<string> {
    if (!cipherText?.startsWith('aes256gcm:')) {
      throw new Error('Ciphertext inválido')
    }
    const raw = fromBase64(cipherText.slice('aes256gcm:'.length))
    if (raw.length < 13) throw new Error('Ciphertext corrompido')
    const iv = raw.slice(0, 12)
    const data = raw.slice(12)
    const key = await this.deriveKey()
    const plainBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data)
    return new TextDecoder().decode(plainBuffer)
  }

  maskPassword(value: string | null | undefined, visible = 0): string {
    if (!value) return '••••••••'
    if (value.startsWith('aes256gcm:')) return '••••••••'
    const safe = value.trim()
    if (safe.length === 0) return '••••••••'
    if (visible <= 0) return '•'.repeat(Math.min(12, Math.max(8, safe.length)))
    const tail = safe.slice(-visible)
    return `${'•'.repeat(Math.max(4, safe.length - visible))}${tail}`
  }

  maskSecret(value: string | null | undefined): string | null {
    if (value == null || value === '') return null
    if (value.startsWith('aes256gcm:')) return this.maskPassword('secret')
    return this.maskPassword(value, 4)
  }

  validate(value: string): void {
    if (typeof value !== 'string') throw new Error('Valor deve ser string')
    if (value.length === 0) throw new Error('Valor vazio não pode ser criptografado')
    if (value.length > 4096) throw new Error('Valor excede limite de criptografia')
  }

  isEncrypted(value: string | null | undefined): boolean {
    return Boolean(value?.startsWith('aes256gcm:'))
  }

  private async deriveKey(): Promise<CryptoKey> {
    const material = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(this.masterSecret),
      'PBKDF2',
      false,
      ['deriveKey'],
    )
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: new TextEncoder().encode('masterhub-supplier-credentials-v1'),
        iterations: 100_000,
        hash: 'SHA-256',
      },
      material,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    )
  }
}

function resolveMasterSecret(): string {
  return env.supplierSecret || 'masterhub-dev-supplier-secret-change-me-32b'
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  bytes.forEach((b) => {
    binary += String.fromCharCode(b)
  })
  return btoa(binary)
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export const encryptionService = new EncryptionService()
